import { isValid } from 'date-fns/isValid'
import { parseISO } from 'date-fns/parseISO'

import { DATE_FORMATS_INTL, formatDate } from '../../../../core/constants'
import type { DataSeries } from '../../../../core'
import type { DataSet } from '../../../../core/types/InsightsInstanceSlotData'
import { buildDynamicCopy } from '../../../../core/utils/Localization'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'
import { buildDataSeries } from '../BarchartBlock/utils'

// The comparison (second) series across every double-bar variant is Income (spend/savings/debt/
// cost-of-living are all "X to Income"). `label` is the stable, non-localized category key, so we
// key off it rather than row position — the wire lists income first within some months.
const INCOME_LABEL = 'income'
const isIncomeRow = (row: { label?: string }): boolean =>
  String(row?.label ?? '').toLowerCase() === INCOME_LABEL

// A date-only wire string ('2026-03-01') is a valid ISO date; anything the backend sends that
// parseISO can't turn into a real Date is dropped before it can poison the ascending sort with a
// NaN comparator (which would scramble column order — and the chartBarIndex the card forwards to the
// drilldown on click).
const hasValidDate = (row: { date?: string }): boolean =>
  typeof row?.date === 'string' && isValid(parseISO(row.date))

// The series name shown to screen readers. `localized_label` should be coming from the backend's already-localized
// display name; Document the backend contract inline so the fallback intent is explicit:
// `localized_label` is expected on every beat whose ariaLabel template uses _3{}.
// `label` ('income'/'spend') is an English-only fallback for dev/error scenarios.
const seriesName = (row: { localized_label?: string; label?: string }): string =>
  String(row?.localized_label ?? row?.label ?? '')

// `amount` is wire data and only loosely typed. `?? 0` guards a missing value but not a non-numeric
// one (a stray string/null would render as NaN in the bar and the aria label), so coerce and fall
// back to 0 unless the result is a finite number.
const toAmount = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Reshapes a flat comparison data_series into one shaped bar per month, sorted ascending by date —
 * the double-bar equivalent of buildChartData. Each month has exactly two contiguous rows; the
 * Income row becomes the comparison series and the other becomes main, regardless of wire order.
 * Months are then sorted ascending so the chart's bar index and the axis line up.
 */
export const buildComparisonSeries = (data: DataSeries | undefined, ariaLabel = ''): DataSet[] => {
  // Guard the wire data before shaping it: `data_series` is type-asserted upstream, not validated, so
  // drop any row without a parseable date rather than let it flow into the sort/group logic.
  const flat = buildDataSeries(data).filter(hasValidDate)
  if (flat.length === 0) {
    return []
  }

  // Bucket rows by calendar month (YYYY-MM), not the exact date string, so a month whose rows carry
  // different day-of-month values (e.g. 2026-01-01 vs 2026-01-15) still groups into one bar rather
  // than two size-1 groups that are then dropped as malformed. First-seen order is preserved so the
  // ascending date sort below is stable.
  const byDate = new Map<string, any[]>()
  for (const row of flat) {
    const monthKey = String(row.date).slice(0, 7)
    const group = byDate.get(monthKey)
    if (group) group.push(row)
    else byDate.set(monthKey, [row])
  }

  // Keep only well-formed months (exactly one main + one comparison row). A month that lost a row to
  // the date guard above, or arrived malformed, is dropped on its own instead of blanking the whole
  // chart; return [] only when no valid month remains.
  const groups = [...byDate.values()].filter((group) => group.length === 2)
  if (groups.length === 0) {
    return []
  }

  return groups
    .map((group) => {
      // Income is always the comparison series; the remaining row is the main category. Fall back to
      // wire order (first = main, last = comparison) if no row is labeled income.
      const comparisonRow = group.find(isIncomeRow) ?? group[group.length - 1]
      const mainRow = group.find((row) => row !== comparisonRow) ?? group[0]
      return { mainRow, comparisonRow }
    })
    .sort(
      ({ mainRow: a }, { mainRow: b }) => parseISO(a.date).getTime() - parseISO(b.date).getTime(),
    )
    .map(({ mainRow, comparisonRow }) => {
      const main = toAmount(mainRow.amount)
      const comparison = toAmount(comparisonRow.amount)
      const monthLong = formatDate(parseISO(mainRow.date), DATE_FORMATS_INTL.MONTH_LONG)

      return {
        // The series name is passed as a third copy slot (_3{}) so a screen-reader user can tell the
        // main bar from the comparison bar. The backend template controls whether/where it renders;
        // when the template only has _1{}/_2{} the extra arg is ignored (no invented text).
        ariaLabelMain: buildDynamicCopy(
          ariaLabel,
          monthLong,
          formatCurrency(main, '0,0.00'),
          seriesName(mainRow),
        ) as string,
        ariaLabelComparison: buildDynamicCopy(
          ariaLabel,
          monthLong,
          formatCurrency(comparison, '0,0.00'),
          seriesName(comparisonRow),
        ) as string,
        comparison,
        date: mainRow.date,
        formattedComparison: formatCurrency(comparison, '0,0'),
        formattedMain: formatCurrency(main, '0,0'),
        label: formatDate(parseISO(mainRow.date), DATE_FORMATS_INTL.MONTH_SHORT),
        main,
      }
    })
}
